import React, {useState} from 'react';
import {BasicTextInput, ColorPicker, Dropdown, HexagonText, Button} from 'web-components';
import DateRangePicker from '../../../forecast-app/shared/components/date-picker/date_picker_v3';
import RichTextField from '../../../components/new-ui/rich_text_field';

import {DATE_PICKER_STYLE, clientLogoSrc, BUDGET_TYPE} from '../../../constants';
import {getDefaultSmallClientLogo} from '../../../components/default_avatars';
import {
	InputLineWrapper,
	SubSection,
	InputLineLabel,
	InputLineInput,
	DatesWrapper,
} from './ProjectSettings_styled';
import LabelDropdown from '../../../components/dropdowns/label-dropdown/Label_dropdown';
import {
	changeName,
	changeDescription,
	changeClient,
	addLabel,
	removeLabel,
	changeStartDate,
	changeEndDate,
	changeProjectColor,
} from './ProjectSettingsMutations';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import Util from '../../../forecast-app/shared/util/util';
import DeleteProjectMutation from '../../../mutations/delete_project_mutation';
import {createToast} from '../../../forecast-app/shared/components/toasts/toast';
import UploadingOverlay from '../../../forecast-app/shared/components/uploading-overlay/uploading_overlay';
import ForecastTooltip from '../../../forecast-app/shared/components/tooltips/ForecastTooltip';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';

const ProjectSettingsGeneral = ({
	intl,
	history,
	projectId,
	projectGroupId,
	projectName,
	projectDoneOrHalted,
	projectDescription,
	projectStartDate,
	projectEndDate,
	companyClients,
	projectClient,
	companyLabels,
	projectLabels,
	projectColor,
	projectPersonsName,
	hasLockedTime,
	hasInvoice,
	budgetType,
	isOldCompany,
	pipedriveCompanyDomain,
	hubspotAccountId,
}) => {
	const [_projectName, setProjectName] = useState(projectName);
	const [_projectColor, setProjectColor] = useState(projectColor);
	const {formatMessage} = intl;

	const labels = projectLabels.map(projectLabel => projectLabel.node.label);
	const labelIds = labels.map(label => label.id);
	const companyLabelsMention = companyLabels.map(companyLabel => companyLabel.node);

	const [showOverlay, setShowOverlay] = useState(false);

	const handleChangeName = name => {
		if (!/\S/.test(name)) {
			setProjectName(projectName);
			return;
		}
		changeName(projectId, name);
	};

	const handleChangeProjectColor = color => {
		let formatedHex = color && color.replace('#', '') + '';
		if (formatedHex && formatedHex.length === 6 && !isNaN(Number('0x' + formatedHex))) {
			setProjectColor(color);
			changeProjectColor(projectId, color);
		}
	};

	const deleteProject = () => {
		const callbackPositive = params => {
			const onSuccess = result => {
				if (
					result.deleteProject &&
					result.deleteProject.errors &&
					result.deleteProject.errors.length === 1 &&
					result.deleteProject.errors[0] === 'TIME_REGISTRATION_IN_LOCKED_PERIOD'
				) {
					showModal({
						type: MODAL_TYPE.WARNING_DATE_LOCKED,
						nonTimeRegWarning: true,
					});
				} else {
					const headers = new Headers();
					headers.append('Content-Type', 'application/json');

					const init = {
						headers: headers,
						credentials: 'include',
						method: 'DELETE',
					};

					if (pipedriveCompanyDomain) {
						fetch(
							process.env.GRAPHQL_ENDPOINT.slice(0, -7) + `pipedrive/project_templates?projectIds=${projectId}`,
							init
						);
					}
					if (hubspotAccountId) {
						fetch(
							process.env.GRAPHQL_ENDPOINT.slice(0, -7) + `hubspot/project_templates?projectIds=${projectId}`,
							init
						);
					}
					createToast({
						duration: 5000,
						message: formatMessage({id: 'settings.has-been-deleted'}),
					});
					history.push('/projects');
				}
			};
			setShowOverlay(true);
			Util.CommitMutation(
				DeleteProjectMutation,
				{
					id: projectId,
					projectGroupId: projectGroupId,
				},
				onSuccess
			);
		};

		showModal({
			type: MODAL_TYPE.CONFIRM_PROJECT_DELETION,
			confirmText: formatMessage({id: 'settings.confirm'}),
			onDelete: callbackPositive,
		});
	};

	const showDuplicateProjectModal = () => {
		const onSuccess = res => {
			history.push('/project/P-' + res.duplicateProject.project.node.companyProjectId + '/settings');
			document.getElementById('project_title').focus();
		};
		showModal({
			type: MODAL_TYPE.NEW_PROJECT_V2,
			projectToDuplicateId: projectId,
			onSuccess,
		});
	};

	const cannotDelete =
		hasLockedTime ||
		hasInvoice ||
		(budgetType === BUDGET_TYPE.RETAINER && !hasPermission(PERMISSION_TYPE.RETAINER_PERIOD_DELETE));

	const duplicateButton = (
		<Button
			size={Button.SIZE.STANDARD}
			variant={Button.VARIANT.VERY_LIGHT_GRAY_OUTLINE}
			onClick={showDuplicateProjectModal}
		>
			{formatMessage({id: 'project_settings.duplicate-project'})}
		</Button>
	);
	const deleteButton = (
		<ForecastTooltip
			content={
				hasInvoice
					? intl.formatMessage({id: 'invoicing.project_invoiced'})
					: hasLockedTime
					? intl.formatMessage({id: 'approval.project_locked_time'})
					: null
			}
		>
			<Button
				size={Button.SIZE.STANDARD}
				variant={Button.VARIANT.RED_OUTLINE}
				onClick={!cannotDelete && deleteProject}
				disabled={cannotDelete}
			>
				{formatMessage({id: 'project-settings.gear-menu.delete-project'})}
			</Button>
		</ForecastTooltip>
	);
	return (
		<>
			<SubSection title={formatMessage({id: 'project_settings.project_details'})}>
				{showOverlay && <UploadingOverlay />}
				<InputLineWrapper>
					<InputLineLabel>{formatMessage({id: 'common.project-name'})}</InputLineLabel>
					<InputLineInput>
						{/*maxLength={191} for an obsucre reason*/}
						<BasicTextInput
							value={_projectName}
							onChange={setProjectName}
							callback={handleChangeName}
							disabled={projectDoneOrHalted}
							maxLength={191}
							cy={'project-name-input'}
						/>
					</InputLineInput>
				</InputLineWrapper>
				<InputLineWrapper>
					<InputLineLabel>{formatMessage({id: 'common.description'})}</InputLineLabel>
					<InputLineInput>
						<RichTextField
							textCy={'project-description-text'}
							cy={'project-description-container'}
							readOnly={projectDoneOrHalted}
							alwaysShowControls={false}
							text={projectDescription}
							//	characterLimit={this.props.viewer.company.characterLimit}
							placeholder={formatMessage({
								id: 'settings.project-edit-description',
							})}
							handleTextChange={description => changeDescription(projectId, description)}
							mentions={projectPersonsName}
							mentionsLabels={companyLabelsMention}
						/>
					</InputLineInput>
				</InputLineWrapper>
				<InputLineWrapper>
					<InputLineLabel>{formatMessage({id: 'common.client'})}</InputLineLabel>
					<InputLineInput>
						<Dropdown
							isClearable
							selectedItems={[projectClient]}
							onSelect={clientId => changeClient(projectId, clientId[0])}
							onRemove={() => changeClient(projectId, null)}
							name={formatMessage({id: 'empty_state_project_client.select-placeholder'})}
							disabled={projectDoneOrHalted}
						>
							{companyClients.map(client => (
								<Dropdown.SingleText
									key={client.node.id}
									value={client.node.id}
									searchString={client.node.name}
								>
									<HexagonText
										key={client.node.id}
										value={client.node.id}
										searchString={client.node.name}
										text={client.node.name}
										imgUrl={
											client.node.logoId
												? clientLogoSrc(client.node.logoId)
												: getDefaultSmallClientLogo(0)
										}
									/>
								</Dropdown.SingleText>
							))}
						</Dropdown>
					</InputLineInput>
				</InputLineWrapper>
				<InputLineWrapper>
					<InputLineLabel>{formatMessage({id: 'project_overview.project_dates'})}</InputLineLabel>
					<InputLineInput>
						<DatesWrapper>
							<DateRangePicker
								clearBothDates={() => changeStartDate(projectId, projectEndDate, null)}
								isSingleDatePicker={true}
								startDate={projectStartDate}
								handleDateRangeChange={startDate => changeStartDate(projectId, projectEndDate, startDate)}
								isNewDateRange={true}
								datePickerStyle={DATE_PICKER_STYLE.SLIM}
								highlighted={true}
								clearable={true}
								disabled={projectDoneOrHalted}
								endDateLimite={projectEndDate}
								hideLimitMessage
							/>
							<DateRangePicker
								clearBothDates={() => changeEndDate(projectId, projectStartDate, null)}
								isSingleDatePicker={true}
								startDate={projectEndDate}
								handleDateRangeChange={endDate => changeEndDate(projectId, projectStartDate, endDate)}
								isNewDateRange={true}
								datePickerStyle={DATE_PICKER_STYLE.SLIM}
								highlighted={true}
								clearable={true}
								disabled={projectDoneOrHalted}
								startDateLimite={projectStartDate}
								hideLimitMessage
							/>
						</DatesWrapper>
					</InputLineInput>
				</InputLineWrapper>
				<InputLineWrapper>
					<InputLineLabel>{formatMessage({id: 'project_settings.project_labels'})}</InputLineLabel>
					<InputLineInput>
						<LabelDropdown
							labels={companyLabels}
							selectedItems={labelIds}
							onSelect={label => addLabel(projectId, projectLabels, label)}
							onRemove={label => removeLabel(projectId, projectLabels, label)}
							selectedGroupName={formatMessage({id: 'common.added'})}
							name={formatMessage({id: 'settings_labels.add-label'})}
							disabled={projectDoneOrHalted}
							showLabelsOnTop
						/>
					</InputLineInput>
				</InputLineWrapper>
				<InputLineWrapper>
					<InputLineLabel>{formatMessage({id: 'project_settings.project-color'})}</InputLineLabel>
					<InputLineInput>
						<ColorPicker
							chooseColor={handleChangeProjectColor}
							selectedColor={_projectColor}
							showHexPicker={isOldCompany}
						/>
					</InputLineInput>
				</InputLineWrapper>
			</SubSection>
			<SubSection
				title={formatMessage({id: 'project_settings.duplicate-project'})}
				actionElement={hasPermission(PERMISSION_TYPE.PROJECTS_CREATE) && duplicateButton}
			></SubSection>
			<SubSection
				title={formatMessage({id: 'project-settings.gear-menu.delete-project'})}
				actionElement={hasPermission(PERMISSION_TYPE.PROJECTS_DELETE) && deleteButton}
			></SubSection>
		</>
	);
};

export default ProjectSettingsGeneral;
